import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth, useUser } from "@clerk/clerk-react";
import { adminService } from "@/lib/api/services";

/**
 * Admin hook for fetching all client profiles
 *
 * Cache Synchronization Strategy:
 * - Uses unified cache key ["client-profiles"]
 * - Pre-populates individual profile caches to avoid redundant requests
 * - Provides seamless integration with the unified profile system
 */
export function useAdminClientProfiles() {
  const { sessionClaims } = useAuth();
  const { user } = useUser();
  const queryClient = useQueryClient();

  // Check if user is admin - check both JWT and fresh publicMetadata to handle stale JWT on first login
  const isAdmin = sessionClaims?.app_role === 'admin' || user?.publicMetadata?.app_role === 'admin';

  const profilesQuery = useQuery({
    queryKey: ["client-profiles"], // Unified cache key
    queryFn: async () => {
      if (!isAdmin) throw new Error("Access denied");

      // Use the new API service
      const profiles = await adminService.getAllClients();
      
      // Pre-populate individual profile caches with the fetched data
      // This keeps everything in sync and avoids redundant network requests
      profiles.forEach(profile => {
        queryClient.setQueryData(["client-profile", profile.id], profile);
      });
      
      return profiles;
    },
    enabled: !!isAdmin,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  return {
    profiles: profilesQuery.data || [],
    isLoading: profilesQuery.isLoading,
    error: profilesQuery.error,
    isAdmin,
  };
}
